import { useState } from 'react'
import { AudioLines, Square } from 'lucide-react'
import { Badge } from '@playbooks/ui/components/badge'
import { Button } from '@playbooks/ui/components/button'
import { Spinner } from '@playbooks/ui/components/spinner'

import { useAppContext } from '../contexts/AppContext'
import useMountEffect from '../hooks/useMountEffect'
import { useTranslation } from '../contexts/localization/Localization'

/**
 * Displays active AI Teammate sessions in the header and provides a one-click stop-all action.
 **/
export default function AiTeammateListeningIndicator() {
  const { activeAiTeammateSessions } = useAppContext()

  if (!activeAiTeammateSessions.length) return null

  // Multiple parallel sessions can be active. Display the earliest-started session's elapsed time
  const startedAt = Math.min(...activeAiTeammateSessions.map(session => session.startedAt))

  const stopAllSessions = async () => {
    await Promise.allSettled(activeAiTeammateSessions.map(session => session.stop()))
  }

  return (
    <div
      data-role="ai-teammate-listening-indicator"
      className="flex shrink-0 items-center justify-self-center py-2"
    >
      <ListeningBadge key={startedAt} startedAt={startedAt} onStop={stopAllSessions} />
    </div>
  )
}

function ListeningBadge({ startedAt, onStop }: { startedAt: number; onStop: () => Promise<void> }) {
  const { text: t } = useTranslation()
  const [elapsedSeconds, setElapsedSeconds] = useState(() => getElapsedSeconds(startedAt))
  const [isStopping, setIsStopping] = useState(false)

  const handleStop = async () => {
    if (isStopping) return

    setIsStopping(true)
    try {
      await onStop()
    } finally {
      setIsStopping(false)
    }
  }

  useMountEffect(() => {
    const timer = window.setInterval(() => setElapsedSeconds(getElapsedSeconds(startedAt)), 1000)
    return () => window.clearInterval(timer)
  })

  return (
    <Badge className="h-7 gap-1.5 rounded-full py-0 pl-2.5 pr-px">
      <AudioLines data-role="ai-teammate-waveform" data-icon="inline-start" aria-hidden="true" />
      {/* Progressively hide nonessential text in narrow cards using container queries (@container is in Head.tsx). */}
      <span className="@max-[340px]:hidden">{t('aiTeammateWidget_headerListening')}</span>
      <span className="tabular-nums text-primary-foreground/60 @max-[440px]:hidden" aria-hidden="true">
        {formatElapsedTime(elapsedSeconds)}
      </span>
      <Button
        data-testid="ai-teammate-indicator-stop-button"
        type="button"
        variant="secondary"
        size="icon-xs"
        className="rounded-full"
        onClick={() => void handleStop()}
        disabled={isStopping}
        aria-busy={isStopping}
        title={t('aiTeammateWidget_stopListening')}
        aria-label={t('aiTeammateWidget_stopListening')}
      >
        {isStopping ? (
          <Spinner data-icon="inline-start" aria-hidden="true" />
        ) : (
          <Square data-icon="inline-start" fill="currentColor" strokeWidth={0} aria-hidden="true" />
        )}
      </Button>
    </Badge>
  )
}

function getElapsedSeconds(startedAt: number) {
  return Math.max(0, Math.floor((Date.now() - startedAt) / 1000))
}

function formatElapsedTime(elapsedSeconds: number) {
  const minutes = Math.floor(elapsedSeconds / 60)
  const seconds = elapsedSeconds % 60
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}
